/**
 * bankCsv — Robuster, clientseitiger Parser für Sparkassen-Kontoauszug-CSV.
 *
 * Format (Feature bank-csv-abgleich):
 *   Trennzeichen: Semikolon `;`
 *   Felder optional in doppelten Anführungszeichen `"..."` (mit `""`-Escape).
 *   Header:
 *     "Auftragskonto";"Buchungstag";"Valutadatum";"Buchungstext";
 *     "Verwendungszweck";"Beguenstigter/Zahlungspflichtiger";"Kontonummer";
 *     "BLZ";"Betrag";"Waehrung";"Info"
 *   Buchungstag/Valutadatum: TT.MM.JJ oder TT.MM.JJJJ.
 *   Betrag: deutsches Format, z. B. "-1.391,77" (Tausenderpunkt, Dezimalkomma).
 *     Negativ = Ausgang, positiv = Eingang.
 *
 * ROBUSTHEIT: Leere Zeilen, abweichende Spaltenzahl und fehlende Spalten
 * führen NICHT zum Absturz — solche Zeilen werden übersprungen und in
 * `uebersprungen` gezählt. Es wird sowohl die exakte Sparkassen-Spaltenfolge
 * unterstützt als auch eine namensbasierte Zuordnung über den Header.
 */

export interface BankCsvZeile {
  /** Verwendungszweck + Empfänger (für import_transaktionen.beschreibung). */
  beschreibung: string;
  buchungDatum: string;   // ISO YYYY-MM-DD
  wertDatum: string | null; // ISO YYYY-MM-DD | null
  betrag: number;         // negativ = Ausgang, positiv = Eingang
  waehrung: string;
  verwendungszweck: string;
  empfaenger: string;
  rohtext: string;        // ganze Originalzeile
}

export interface BankCsvParseErgebnis {
  zeilen: BankCsvZeile[];
  /** Anzahl Zeilen, die wegen leerem/ungültigem Inhalt übersprungen wurden. */
  uebersprungen: number;
  /** Warnungen (z. B. unbekannter Header) — rein informativ. */
  warnungen: string[];
}

/** Deutsches Zahlenformat "-1.391,77" → -1391.77. Liefert NaN bei Unparsebarem. */
export function parseDeutscherBetrag(raw: string): number {
  if (raw == null) return NaN;
  let s = String(raw).trim();
  if (!s) return NaN;
  // Währungszeichen / Leerzeichen entfernen
  s = s.replace(/[€\s]/g, '');
  // Tausenderpunkte entfernen, Dezimalkomma → Punkt
  s = s.replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/** TT.MM.JJ oder TT.MM.JJJJ → ISO YYYY-MM-DD. Liefert null bei Unparsebarem. */
export function parseDeutschesDatum(raw: string): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);
  if (!m) return null;
  const tag = m[1].padStart(2, '0');
  const monat = m[2].padStart(2, '0');
  let jahr = m[3];
  if (jahr.length === 2) {
    // 2-stellig: 00–69 → 2000er, 70–99 → 1900er
    const j = parseInt(jahr, 10);
    jahr = (j <= 69 ? 2000 + j : 1900 + j).toString();
  }
  // Plausibilitätsprüfung
  const monatN = parseInt(monat, 10);
  const tagN = parseInt(tag, 10);
  if (monatN < 1 || monatN > 12 || tagN < 1 || tagN > 31) return null;
  // Echte Kalender-Validierung: existiert das Datum wirklich?
  // (z. B. 30.02. oder 31.04. ergeben sonst einen ungültigen ISO-String,
  // den Postgres ablehnt.)
  const dt = new Date(Number(jahr), monatN - 1, tagN);
  if (
    dt.getFullYear() !== Number(jahr) ||
    dt.getMonth() !== monatN - 1 ||
    dt.getDate() !== tagN
  ) {
    return null;
  }
  return `${jahr}-${monat}-${tag}`;
}

/**
 * Bereinigt einen rohen SEPA-Verwendungszweck für die Anzeige.
 *
 * Sparkassen-Exporte hängen oft strukturierte SEPA-Tokens an den
 * Verwendungszweck (EREF+, MREF+, CRED+, SVWZ+, ABWA+, KREF+, IBAN+, BIC+).
 * Diese Tokens samt nachfolgendem Wert werden entfernt, sodass nur der
 * lesbare Klartext übrig bleibt. Der eigentliche SVWZ-Inhalt (der echte
 * Verwendungszweck) wird beibehalten — lediglich das Token-Präfix entfernt.
 *
 * WICHTIG: AK-Nummern (z. B. "AK123456") werden NICHT entfernt — sie sind für
 * die Akten-/Rechnungszuordnung relevant.
 */
export function cleanVerwendungszweck(roh: string): string {
  if (!roh) return '';
  let s = String(roh);

  // Strukturierte SEPA-Tokens, deren Wert NICHT angezeigt werden soll
  // (Referenzen / Identifikatoren ohne Mehrwert für die Anzeige):
  // EREF (End-to-End-Ref), MREF (Mandatsref), CRED (Creditor-ID),
  // ABWA (abw. Auftraggeber), KREF (Kundenref), IBAN, BIC.
  // Diese Tokens + ihr Wert (bis zum nächsten Token oder Ende) werden entfernt.
  const ENTFERN_TOKENS = ['EREF', 'MREF', 'CRED', 'ABWA', 'KREF', 'IBAN', 'BIC'];
  // SVWZ trägt den echten Verwendungszweck — nur das Präfix entfernen, Wert behalten.
  const ALLE_TOKENS = ['EREF', 'MREF', 'CRED', 'SVWZ', 'ABWA', 'KREF', 'IBAN', 'BIC'];

  // Lookahead, der auf das nächste Token (TOKEN+) oder das Zeilenende matcht.
  const naechstesToken = `(?=\\s*(?:${ALLE_TOKENS.join('|')})\\+|$)`;

  // 1) Zu entfernende Tokens samt Wert tilgen.
  for (const t of ENTFERN_TOKENS) {
    const re = new RegExp(`${t}\\+.*?${naechstesToken}`, 'gi');
    s = s.replace(re, ' ');
  }

  // 2) Verbleibende Token-Präfixe (insb. SVWZ+) entfernen, Wert behalten.
  for (const t of ALLE_TOKENS) {
    s = s.replace(new RegExp(`${t}\\+`, 'gi'), ' ');
  }

  // 3) Mehrfache Trenner/Leerzeichen normalisieren.
  s = s
    .replace(/\s*·\s*/g, ' · ')   // bestehende Punkt-Trenner sauber halten
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s·]+|[\s·]+$/g, '')
    .trim();

  return s;
}

/**
 * Zerlegt eine einzelne CSV-Zeile in Felder.
 * Berücksichtigt Anführungszeichen (inkl. `""`-Escape) und Semikolon-Trenner.
 */
function splitCsvZeile(zeile: string): string[] {
  const felder: string[] = [];
  let aktuell = '';
  let inQuotes = false;
  for (let i = 0; i < zeile.length; i++) {
    const c = zeile[i];
    if (inQuotes) {
      if (c === '"') {
        if (zeile[i + 1] === '"') {
          aktuell += '"';
          i++; // escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        aktuell += c;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
      } else if (c === ';') {
        felder.push(aktuell);
        aktuell = '';
      } else {
        aktuell += c;
      }
    }
  }
  felder.push(aktuell);
  return felder.map((f) => f.trim());
}

/** Normalisiert einen Header-Namen für robusten Vergleich. */
function normHeader(h: string): string {
  return h.toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Bekannte Sparkassen-Spalten (Reihenfolge) — Fallback, falls Header fehlt.
const STANDARD_SPALTEN = [
  'auftragskonto', 'buchungstag', 'valutadatum', 'buchungstext',
  'verwendungszweck', 'beguenstigterzahlungspflichtiger', 'kontonummer',
  'blz', 'betrag', 'waehrung', 'info',
];

/**
 * Parst den kompletten CSV-Text (Sparkassen-Format).
 * Wirft NICHT — gibt immer ein Ergebnis zurück (ggf. leer + Warnung).
 */
export function parseSparkassenCsv(text: string): BankCsvParseErgebnis {
  const warnungen: string[] = [];
  const zeilen: BankCsvZeile[] = [];
  let uebersprungen = 0;

  if (!text || !text.trim()) {
    return { zeilen, uebersprungen, warnungen: ['Datei ist leer.'] };
  }

  // BOM entfernen
  const clean = text.replace(/^﻿/, '');
  // Zeilen splitten (CRLF/LF/CR)
  const rohZeilen = clean.split(/\r\n|\n|\r/);

  if (rohZeilen.length === 0) {
    return { zeilen, uebersprungen, warnungen: ['Keine Zeilen gefunden.'] };
  }

  // Header analysieren
  const headerFelder = splitCsvZeile(rohZeilen[0]).map(normHeader);
  const hatHeader = headerFelder.includes('buchungstag') && headerFelder.includes('betrag');

  // Spalten-Index-Map aufbauen
  const idx: Record<string, number> = {};
  const quelle = hatHeader ? headerFelder : STANDARD_SPALTEN;
  quelle.forEach((name, i) => {
    if (!(name in idx)) idx[name] = i;
  });

  if (!hatHeader) {
    warnungen.push('Kein Standard-Header erkannt — Spalten werden positionsbasiert (Sparkassen-Standard) interpretiert.');
  }

  const getFeld = (felder: string[], name: string): string => {
    const i = idx[name];
    if (i === undefined || i < 0 || i >= felder.length) return '';
    return felder[i] ?? '';
  };

  const startIdx = hatHeader ? 1 : 0;
  for (let r = startIdx; r < rohZeilen.length; r++) {
    const roh = rohZeilen[r];
    if (!roh || !roh.trim()) {
      // komplett leere Zeile — still überspringen, nicht zählen
      continue;
    }
    const felder = splitCsvZeile(roh);

    const buchungRaw = getFeld(felder, 'buchungstag');
    const betragRaw = getFeld(felder, 'betrag');
    const buchungDatum = parseDeutschesDatum(buchungRaw);
    const betrag = parseDeutscherBetrag(betragRaw);

    // Mindest-Validität: gültiges Datum + gültiger Betrag
    if (!buchungDatum || Number.isNaN(betrag)) {
      uebersprungen++;
      continue;
    }

    const wertDatum = parseDeutschesDatum(getFeld(felder, 'valutadatum'));
    const verwendungszweck = getFeld(felder, 'verwendungszweck');
    const empfaenger = getFeld(felder, 'beguenstigterzahlungspflichtiger');
    const buchungstext = getFeld(felder, 'buchungstext');
    let waehrung = getFeld(felder, 'waehrung').trim().toUpperCase();
    if (!waehrung) waehrung = 'EUR';

    // beschreibung = Verwendungszweck + Empfänger (+ Buchungstext als Fallback)
    const beschreibungTeile = [verwendungszweck, empfaenger, buchungstext]
      .map((t) => t.trim())
      .filter(Boolean);
    const beschreibung = beschreibungTeile.join(' · ') || '(ohne Beschreibung)';

    zeilen.push({
      beschreibung,
      buchungDatum,
      wertDatum,
      betrag,
      waehrung,
      verwendungszweck: verwendungszweck.trim(),
      empfaenger: empfaenger.trim(),
      rohtext: roh,
    });
  }

  if (zeilen.length === 0 && uebersprungen === 0) {
    warnungen.push('Keine gültigen Buchungszeilen gefunden.');
  }

  return { zeilen, uebersprungen, warnungen };
}

/** Aggregierte Kennzahlen für die Import-Vorschau. */
export interface BankCsvVorschau {
  anzahl: number;
  von: string | null;
  bis: string | null;
  summe: number;
  summeEingang: number;
  summeAusgang: number;
}

export function berechneVorschau(zeilen: BankCsvZeile[]): BankCsvVorschau {
  if (zeilen.length === 0) {
    return { anzahl: 0, von: null, bis: null, summe: 0, summeEingang: 0, summeAusgang: 0 };
  }
  let von = zeilen[0].buchungDatum;
  let bis = zeilen[0].buchungDatum;
  let summe = 0;
  let summeEingang = 0;
  let summeAusgang = 0;
  for (const z of zeilen) {
    if (z.buchungDatum < von) von = z.buchungDatum;
    if (z.buchungDatum > bis) bis = z.buchungDatum;
    summe += z.betrag;
    if (z.betrag > 0) summeEingang += z.betrag;
    else summeAusgang += z.betrag;
  }
  return { anzahl: zeilen.length, von, bis, summe, summeEingang, summeAusgang };
}
